import { useAuth } from '../context/AuthContext';
import { useMemo } from 'react';

// Function to check if a menu item path is accessible to the user
const isPathAccessible = (path, allowedPages) => {
  if (!path) return false;
  
  const normalize = (p) => String(p || '').trim().replace(/^\/+/, '');
  const allowed = Array.isArray(allowedPages) ? allowedPages.map(normalize) : [];
  const normalizedPath = normalize(path);

  // Master admin can access everything
  if (allowed.includes('*')) return true;
  
  // Dashboard access is now controlled by allowedPages - no special treatment
  
  // Check if the exact path is allowed
  if (allowed.includes(normalizedPath)) return true;
  
  // Check if any parent path is allowed (for nested routes)
  const pathSegments = normalizedPath.split('/').filter(segment => segment);
  for (let i = pathSegments.length; i > 0; i--) {
    const parentPath = pathSegments.slice(0, i).join('/');
    if (allowed.includes(parentPath)) return true;
  }
  
  return false;
};

// Recursive function to filter menu items based on user permissions
const filterMenuItems = (menuItems, allowedPages, userRole = null) => {
  if (!menuItems || !Array.isArray(menuItems)) return [];
  
  return menuItems
    .map(item => {
      // Role-specific filtering
      if (userRole === 'custom_role_ck' && item.label === 'Branch') {
        return null; // Hide Branch submenu for CK users
      }
      // Sales and order-receiver must never see Central Kitchen menu
      if ((userRole === 'custom_role_sales' || userRole === 'custom_role_order_receiver') && item.label === 'Central Kitchen') {
        return null;
      }

      // Sales menu: sales user sees Dashboard, New Order, Orders, Revenue; admin sees Orders, Revenue, Edit Menu
      if ((item.label === 'Sales' || item.label === 'Bike Sales') && item.children && Array.isArray(item.children)) {
        const isSalesUser = userRole && String(userRole).toLowerCase() === 'custom_role_sales';
        const isAdminOrMaster = userRole === 'admin' || userRole === 'master_admin';
        let children = item.children;
        if (isSalesUser) {
          children = children.filter(
            (c) =>
              c.path !== '/sales/orders-history' &&
              c.path !== '/sales/revenue' &&
              c.path !== '/sales/cash-handover' &&
              c.path !== '/sales/cash-handover-history' &&
              c.path !== '/sales/edit-menu' &&
              (c.path === '/sales/customers' ||
                c.path === '/sales/customers/create' ||
                c.path === '/sales/unpaid-orders' ||
                c.path === '/sales/customer-debt' ||
                isPathAccessible(c.path, allowedPages))
          );
        } else if (isAdminOrMaster) {
          children = children.filter(
            (c) =>
              (c.path === '/sales/orders' ||
                c.path === '/sales/unpaid-orders' ||
                c.path === '/sales/customer-debt' ||
                c.path === '/sales/orders-history' ||
                c.path === '/sales/revenue' ||
                c.path === '/sales/cash-handover' ||
                c.path === '/sales/cash-handover-history' ||
                c.path === '/sales/edit-menu' ||
                c.path === '/sales/customers' ||
                c.path === '/sales/menu') &&
              isPathAccessible(c.path, allowedPages)
          );
        } else {
          children = filterMenuItems(item.children, allowedPages, userRole);
        }
        if (children.length > 0) {
          return { ...item, children };
        }
        return null;
      }
      
      // Order Receiver: only for custom_role_order_receiver (hidden from admin/master_admin)
      if (item.label === 'Order Receiver' && item.children) {
        if (userRole === 'admin' || userRole === 'master_admin') return null;
        const canAccess = isPathAccessible('/order-receiver', allowedPages) || isPathAccessible('order-receiver', allowedPages);
        if (!canAccess) return null;
        const filteredChildren = filterMenuItems(item.children, allowedPages, userRole);
        if (filteredChildren.length > 0) return { ...item, children: filteredChildren };
        return null;
      }
      
      // If item has a direct path, check if it's accessible
      if (item.path) {
        if (isPathAccessible(item.path, allowedPages)) {
          return item;
        }
        return null;
      }
      
      // If item has children, recursively filter them
      if (item.children && Array.isArray(item.children)) {
        const filteredChildren = filterMenuItems(item.children, allowedPages, userRole);
        if (filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        }
        return null;
      }
      
      return item;
    })
    .filter(Boolean);
};

// Hook to get filtered menu items based on current user permissions
export const useFilteredMenu = (menuItems) => {
  const { getAllowedPages, user } = useAuth();
  
  const filteredMenu = useMemo(() => {
    if (!user || !menuItems) return [];
    const allowedPages = getAllowedPages();
    if (!allowedPages || allowedPages.length === 0) return [];
    return filterMenuItems(menuItems, allowedPages, user.role);
  }, [user, menuItems, getAllowedPages]);
  
  return filteredMenu;
};

// Export the filter function for direct use
export { filterMenuItems, isPathAccessible };
